import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import * as moment from 'moment';
import { ClientMealTypeService } from 'src/app/providers/client-meal-type.service';
import { ToastService } from 'src/app/providers/toast.service';
import { IMealType } from 'src/app/models/meal_type';
import { ExternalDocumentsService } from '../e-signature/e-signature.service';
import { IExternalDocument } from 'src/app/models/external_document';
import { ShowDocumentModal } from 'src/app/pages/clients/client-detail/documents/show-document.modal';


@Component({
  selector: 'a180-meal-plan-config-modal',
  templateUrl: './meal-plan-config-modal.component.html',
  styleUrls: ['./meal-plan-config-modal.component.scss']
})
export class MealPlanConfigModalComponent implements OnInit {
  @Input() clientId: number;
  @Input() externalDocument: IExternalDocument;
  @Input() clientFormValues: any = {};
  @Input() programId: number;
  @Input() date: string;

  form: FormGroup;
  isLoading = true;
  isSaving = false;
  isFetchingPreview = false;

  get today(): string {
    return this.date || moment().format('YYYY-MM-DD');
  }

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private modalCtrl: ModalController,
    private clientMealTypeService: ClientMealTypeService,
    private externalDocumentsService: ExternalDocumentsService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadMealTypes();
  }

  get mealsArray(): FormArray {
    return this.form?.get('meals') as FormArray;
  }

  get isProcessing(): boolean {
    return this.isSaving || this.isFetchingPreview;
  }

  asFormGroup(ctrl: AbstractControl): FormGroup {
    return ctrl as FormGroup;
  }

  loadMealTypes() {
    this.clientMealTypeService.fetchClientMealTypes(this.clientId, this.today, this.externalDocument?.id).subscribe(
      data => {
        this.buildForm(data.meal_types, data.default_meal_types, data.default_metadata);
        this.isLoading = false;
        if (data.alert) {
          this.toastService.handleResponse(data.alert, false, 'success');
        }
      },
      () => {
        this.toastService.handleResponse('Failed to load meal configuration', true, 'danger');
        this.isLoading = false;
      }
    );
  }

  buildForm(mealTypes: IMealType[], defaultMealTypes: IMealType[], defaultMetadata: { healthy_fats?: string, bars_allowed?: boolean } = {}) {
    const configuredByName = new Map(mealTypes.map(m => [m.name, m]));
    const isNewPlan = mealTypes.length === 0;

    const rows: Array<IMealType & { included: boolean }> = defaultMealTypes.map(d => {
      const configured = configuredByName.get(d.name);
      return configured
        ? { ...configured, included: true }
        : { ...d, included: isNewPlan && !d.optional && d.status !== 'inactive' };
    });

    this.form = this.fb.group({
      plan_name: [this.clientFormValues?.plan_name || 'Meal Plan', Validators.required],
      healthy_fats: [this.clientFormValues?.healthy_fats || defaultMetadata?.healthy_fats || '2-4 tbsp', Validators.required],
      bars_allowed: [this.clientFormValues?.bars_allowed ?? defaultMetadata?.bars_allowed ?? true],
      meals: this.fb.array(rows.map((row, i) => this.createMealGroup(row, i)))
    });
  }

  createMealGroup(meal: IMealType & { included: boolean }, index: number): FormGroup {
    return this.fb.group({
      id: meal.id,
      name: meal.name,
      display_name: meal.display_name,
      position: index,
      included: meal.included,
      start_time: this.extractTime(meal.start_time),
      end_time: this.extractTime(meal.end_time),
      meal_mode: meal.oz_protein != null || meal.kind === 'protein' ? 'protein' : 'products',
      number_of_products: meal.number_of_products ?? 1,
      oz_protein: meal.oz_protein ?? null,
      healthy_fats: meal.healthy_fats || meal.client_meal_type?.healthy_fats || null,
      veggies: meal.veggies ?? null
    });
  }

  extractTime(isoString: string): string {
    if (!isoString) return '';
    if (/^\d{2}:\d{2}$/.test(isoString)) return isoString;
    return moment.parseZone(isoString).format('HH:mm');
  }

  drop(event: CdkDragDrop<AbstractControl[]>) {
    const arr = this.mealsArray;
    const item = arr.at(event.previousIndex);
    arr.removeAt(event.previousIndex);
    arr.insert(event.currentIndex, item);
    arr.controls.forEach((ctrl, i) => ctrl.get('position').setValue(i));
  }

  save() {
    this.form.markAllAsTouched();

    const invalidMealIndex = this.mealsArray.controls.findIndex(ctrl => {
      if (!ctrl.get('included').value) return false;
      const v = ctrl.value;
      if (!v.start_time) return true;
      if (v.meal_mode === 'protein') return !v.oz_protein || !this.isValidInteger(v.oz_protein);
      return !v.number_of_products || !this.isValidInteger(v.number_of_products);
    });

    if (this.form.invalid || invalidMealIndex > -1) {
      this.reportInvalid(invalidMealIndex);
      return;
    }

    this.isSaving = true;
    this.clientFormValues.plan_name = this.form.get('plan_name').value;
    this.clientFormValues.healthy_fats = this.form.get('healthy_fats').value;
    this.clientFormValues.bars_allowed = this.form.get('bars_allowed').value;
    const payload = this.mealsArray.controls
      .filter(ctrl => ctrl.get('included').value)
      .map((ctrl, i) => {
        const v = ctrl.value;
        return {
          id: v.id,
          position: i,
          status: 'active',
          number_of_products: v.meal_mode === 'protein' ? null : parseInt(v.number_of_products, 10),
          oz_protein: v.meal_mode === 'protein' ? parseInt(v.oz_protein, 10) : null,
          healthy_fats: v.healthy_fats || null,
          veggies: v.veggies || null,
          start_time: v.start_time || null,
          end_time: v.end_time || null
        };
      });

    this.clientMealTypeService.updateClientMealTypes(this.clientId, payload as any, this.today, this.externalDocument?.id).subscribe(
      () => {
        this.isSaving = false;
        this.openPreview();
      },
      () => {
        this.isSaving = false;
        this.toastService.handleResponse('Failed to save meal plan', true, 'danger');
      }
    );
  }

  private reportInvalid(mealIndex: number) {
    this.toastService.handleResponse('Please complete the highlighted fields before saving.', false, 'danger');

    const host = this.elementRef.nativeElement as HTMLElement;
    const target = this.form.invalid
      ? host.querySelector('.config-section')
      : host.querySelectorAll('.meal-item')[mealIndex];
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private openPreview() {
    this.isFetchingPreview = true;
    this.externalDocumentsService.fetchHtmlOfExternalDocument(
      this.externalDocument,
      this.clientFormValues,
      this.programId,
      this.today
    ).subscribe(
      async html => {
        this.isFetchingPreview = false;
        const preview = await this.modalCtrl.create({
          component: ShowDocumentModal,
          componentProps: {
            document_name: this.externalDocument?.name,
            htmlContent: html,
            canSend: true,
            fileType: 'html'
          },
          cssClass: 'modal-fullscreen'
        });
        await preview.present();
        const { data } = await preview.onDidDismiss();
        if (data?.sendDocument) {
          this.modalCtrl.dismiss({ saved: true, sendDocument: true, clientFormValues: this.clientFormValues });
        }
      },
      () => {
        this.isFetchingPreview = false;
        this.toastService.handleResponse('Failed to load preview', true, 'danger');
      }
    );
  }

  isValidInteger(value: any): boolean {
    return /^\d+$/.test(String(value)) && parseInt(value, 10) > 0;
  }

  toIsoTime(hhmm: string): string {
    if (!hhmm) return null;
    return `2000-01-01T${hhmm}:00`;
  }

  onTimeChange(event: any, ctrl: AbstractControl, field: string) {
    const raw: string = event.detail.value;
    if (raw) {
      ctrl.get(field)?.setValue(moment.parseZone(raw).format('HH:mm'));
      ctrl.get(field)?.markAsTouched();
    }
  }

  clearField(ctrl: AbstractControl, field: string) {
    ctrl.get(field).setValue('');
  }

  dismiss() {
    this.modalCtrl.dismiss({ saved: false });
  }
}
